import { createContext, useContext, useCallback, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * One app-wide confirmation pattern (audit §10 #15 / docs/37 P2).
 *
 * Replaces the scattered native `window.confirm()` calls — which are
 * unstyled, un-themeable, and not keyboard/screen-reader consistent — with a
 * single promise-based dialog built on the shadcn AlertDialog (Radix, so it
 * gets focus-trap + Escape + ARIA roles for free).
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Remove Mom?',
 *     description: 'This permanently deletes her profile.',
 *     confirmText: 'Remove',
 *     destructive: true,
 *   });
 *   if (!ok) return;
 *
 * The promise resolves `true` on confirm, `false` on cancel / Escape /
 * backdrop — so it drops into the old `if (!window.confirm(...)) return;`
 * shape as `if (!(await confirm({...}))) return;`.
 */

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
};

const DEFAULTS = {
  title: 'Are you sure?',
  description: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  destructive: false,
};

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null); // null = closed
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts({ ...DEFAULTS, ...options });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  // Settle exactly once — Radix fires onOpenChange(false) in addition to the
  // button onClick, so guard on the resolver ref to avoid a double-resolve.
  const settle = useCallback((result) => {
    setOpts(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(result);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!opts} onOpenChange={(open) => { if (!open) settle(false); }}>
        {opts && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{opts.title}</AlertDialogTitle>
              {opts.description ? (
                <AlertDialogDescription>{opts.description}</AlertDialogDescription>
              ) : null}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => settle(false)}>
                {opts.cancelText}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => settle(true)}
                className={opts.destructive ? cn(buttonVariants({ variant: 'destructive' })) : undefined}
              >
                {opts.confirmText}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
